import {
    buildTableFilterParams,
    type TableApi,
} from '@ics-portal/ics-data-table';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { type Component } from '@gql/graphql';
import TitleWithHelpLink from '@components/_molecules/TitleWithHelpLink';
import ContentController from '@components/_organisms/Headers/ContentController';
import { InfoCardTemplate } from '@components/_templates/InfoCardTemplate';
import { useInfoCard } from '@components/InfoCard';
import { NewComponentCreateRequestTrigger } from '@components/NewComponentCreateRequestTrigger/NewComponentsCreateRequestTrigger';
import { type Maybe } from '@models/Maybe';
import { useComponentCreateRequestDetails } from '@hooks/NewComponentRequests/useComponentCreateRequestDetails';
import { type ComponentRow } from '@pages/notification-components/components/ComponentsCard/columns';
import { type RequestRow } from '@pages/notification-components/components/RequestsCard/RequestsTable/columns';
import { ComponentsCard, InfoCard, RequestsCard } from './components';

export default function NotificationComponentsPage(): ReactNode {
    const { setOpen } = useInfoCard();
    const [searchParams, setSearchParams] = useSearchParams();
    const idParam = searchParams.get('id');

    const [selectedComponent, setSelectedComponent] =
        useState<Component | null>(null);
    const [selectedRequests, setSelectedRequests] = useState<RequestRow[]>([]);
    const componentsApi = useRef<TableApi<ComponentRow>>(null);
    const requestsApi = useRef<TableApi<RequestRow>>(null);
    const selectedRequest = useComponentCreateRequestDetails(
        selectedRequests.length === 1 ? selectedRequests[0] : null,
    );

    useEffect(() => {
        if (idParam) {
            const nextParams = new URLSearchParams(searchParams);
            nextParams.delete('id');
            const tableFilterParams = buildTableFilterParams(
                'notification-components',
                [{ key: 'id', operator: 'equals', value: idParam }],
            );
            for (const [k, v] of tableFilterParams.entries()) {
                nextParams.set(k, v);
            }
            setSearchParams(nextParams, { replace: true });
        }
    }, [idParam, searchParams, setSearchParams]);

    useEffect(() => {
        setOpen(Boolean(selectedComponent) || selectedRequests.length > 0);
    }, [selectedComponent, selectedRequests.length, setOpen]);

    function handleRequestChange(nextRequests: RequestRow[]): void {
        setSelectedRequests(nextRequests);
        if (nextRequests.length > 0) {
            componentsApi.current?.clearSelection();
        }
    }

    function handleComponentChange(component: Maybe<Component>): void {
        setSelectedComponent(component ?? null);
        if (component) {
            requestsApi.current?.clearSelection();
        }
    }

    return (
        <InfoCardTemplate
            infoCard={
                <InfoCard
                    component={selectedComponent}
                    request={selectedRequest}
                    requests={selectedRequests}
                />
            }
        >
            <ContentController
                title={
                    <TitleWithHelpLink
                        label="Notification components"
                        link="https://wiki.siemens-energy.com/x/PW8ZGg"
                    />
                }
            />
            <div className="p-content flex flex-column gap-4">
                <NewComponentCreateRequestTrigger />
                <RequestsCard
                    ref={requestsApi}
                    selectedChange={handleRequestChange}
                />
                <ComponentsCard
                    ref={componentsApi}
                    selectedChange={handleComponentChange}
                />
            </div>
        </InfoCardTemplate>
    );
}
